import { PhoneNumberUtil } from 'google-libphonenumber'
import moment from 'moment'

import {
	getStandardValidationMessage,
	getValidationMessageForInvalidPhoneNumber,
} from '@/common/forms/validationMessage'
import { TFunction } from '@/lib/i18n/i18n'

const ALPHANUMERIC_PATTERN = /^[a-zA-Z0-9]+$/
const ENDS_WITH_TWO_DIGITS_PATTERN = /\d{2}$/
const STARTS_WITH_THREE_LETTERS_PATTERN = /^[a-zA-Z]{3}/

export const ALPHANUMERIC_WITH_SPECIALS_REGEXP = /^[a-zA-Z0-9!"#$%&'()*+,-./:;<=>?@[\\\]^_`{|}~\s]+$/

const phoneUtil = PhoneNumberUtil.getInstance()

export const validatePhoneNumber = (value: string) => {
	if (!value) {
		return false
	}

	try {
		const number = phoneUtil.parseAndKeepRawInput(value)
		return phoneUtil.isValidNumber(number)
	} catch {
		return false
	}
}

export function getValidationRules(t: TFunction) {
	const message = getStandardValidationMessage(t)

	const rules = {
		required: (value: boolean, customMessage?: string) => ({
			required: value ? customMessage || message.required : false,
		}),
		min: (value: number, customMessage?: string) => ({
			min: {
				value,
				message: customMessage || message.minValue(value),
			},
		}),
		max: (value: number, customMessage?: string) => ({
			max: {
				value,
				message: customMessage || message.maxValue(value) || '',
			},
		}),
		minLength: (value: number, customMessage?: string) => ({
			minLength: {
				value,
				message: customMessage || message.minLength(value) || '',
			},
		}),
		maxLength: (value: number, customMessage?: string) => ({
			maxLength: {
				value,
				message: customMessage || message.maxLength(value) || '',
			},
		}),
		alphanumeric: (customMessage?: string) => ({
			pattern: {
				value: ALPHANUMERIC_PATTERN,
				message: customMessage || message.alphanumeric,
			},
		}),
		predefinedPatterns: (
			value: { last_two_digits?: boolean; first_three_letters?: boolean },
			customMessage?: string,
		) => {
			if (value.last_two_digits && value.first_three_letters) {
				return {
					pattern: {
						value: new RegExp([STARTS_WITH_THREE_LETTERS_PATTERN.source, ENDS_WITH_TWO_DIGITS_PATTERN.source].join('')),
						message: customMessage || message.firstThreeLettersAndLastTwoDigits,
					},
				}
			}

			if (value.last_two_digits) {
				return {
					pattern: {
						value: ENDS_WITH_TWO_DIGITS_PATTERN,
						message: customMessage || message.lastTwoDigits,
					},
				}
			}
			if (value.first_three_letters) {
				return {
					pattern: {
						value: STARTS_WITH_THREE_LETTERS_PATTERN,
						message: customMessage || message.firstThreeLetters,
					},
				}
			}
			return {}
		},
		phoneNumber: () => ({
			validate: (value: string) =>
				!value || validatePhoneNumber(value) ? true : getValidationMessageForInvalidPhoneNumber(t),
		}),
	}

	return {
		...rules,
		strictLength: (value: number, customMessage?: string) => {
			const validationMessage = customMessage || message.strictLength(value)

			return {
				...rules.minLength(value, validationMessage),
				...rules.maxLength(value, validationMessage),
			}
		},
	}
}

export function getNamePattern(minLength = 1) {
	return `^([\x20]*[a-zA-Z\s]+([-]+[a-zA-Z]+)*[\x20]*){${minLength},}$` // eslint-disable-line no-useless-escape
}

export const isName = (value: string) => new RegExp(getNamePattern()).test(value.trim())
export const isAlphanumeric = (value: string) => ALPHANUMERIC_PATTERN.test(value)
export const isPassportNumber = (value: string) => {
	const valueLength = value.trim().length
	return isAlphanumeric(value) && valueLength >= 6 && valueLength <= 9
}
export const isNationalIdNumber = (value: string) => {
	const valueLength = value.trim().length
	return isAlphanumeric(value) && valueLength >= 5 && valueLength <= 16
}
export const isLastName = (value: string) => isName(value) && value.replace(/-/g, '').trim().length >= 2
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isDateExpired = (date: any) => {
	const validExpirationDate = moment().subtract(1, 'days').toDate()
	return new Date(date) <= new Date(validExpirationDate)
}

export const isValidLength = (value: string, length: number) => {
	return value.replace(/-/g, '').trim().length >= length
}
